import {
    MouseEvent as ReactMouseEvent,
    PropsWithChildren,
    useEffect,
    useRef,
    useState,
} from 'react'

import { GenericStorage } from '../../../tools/dom/storage'

interface ResizableColumnProps extends PropsWithChildren {
    defaultWidth: number
    minWidth?: number
    maxWidth: number
    storageKey?: string
}
export default function ResizableWidth({
    defaultWidth,
    minWidth,
    maxWidth,
    storageKey,
    children,
}: ResizableColumnProps) {
    const widthStorage = storageKey ? new GenericStorage<number>(storageKey) : undefined
    minWidth = minWidth ?? defaultWidth
    const startWidth = widthStorage?.read() ?? defaultWidth
    const [dragging, setDragging] = useState(false)
    const [width, setWidth] = useState(startWidth)
    const [offset, setOffset] = useState(0)
    const [dragX, setDragX] = useState(0)
    const ref = useRef(null)

    const calculateX = (event: MouseEvent) => clampX(event.clientX - offset)
    const clampX = (x: number) => Math.min(Math.max(x, minWidth), maxWidth)

    useEffect(() => {
        const element = ref.current
        if (!element) return
        const div = element as HTMLDivElement
        const offset = div.offsetLeft
        const width = div.clientWidth
        setWidth(width)
        setOffset(offset)
    }, [ref])

    const onMouseMove = (event: MouseEvent) => {
        event.preventDefault()
        const x = calculateX(event)
        setDragX(x)
    }

    const onMouseUp = (event: MouseEvent) => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        const x = calculateX(event)
        widthStorage?.store(x)
        setWidth(x)
        setDragging(false)
        setDragX(0)
    }

    // NOTE: We only call mousedown a single time after initialization of width and offset.
    // This cannot happen before the user starts the drag and cannot happen again before the drag ends,
    // Because of this flow of control, it is okay to not hook wildly into react hooks and event cycle.
    const onMouseDown = (event: ReactMouseEvent) => {
        event.preventDefault()
        setDragging(true)
        setDragX(event.clientX - offset)
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }

    return (
        <div className="resizable-width" ref={ref} style={{ width: width }}>
            {children}
            <div className="resizable-width__handle" onMouseDown={onMouseDown} />
            {dragging && <div className="resizable-width__indicator" style={{ left: dragX }} />}
        </div>
    )
}
